import type {
  ApiClubMember,
  ApiQueueEntry,
  Equipment,
  Evaluation,
  LeaderboardEntry,
  Match,
  Member,
} from '@/types';
import { buildAvatarDataUri } from './avatarBuilder';

const clampSkill = (val: number | null | undefined, fallback: number) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return fallback;
  return Math.max(1, Math.min(5, Number(val)));
};

const normalizeCollectionName = (collection?: string | null) => {
  if (!collection) return undefined;
  const key = collection.trim();
  const map: Record<string, string> = {
    'toonhead': 'toon-head',
    'toon-head': 'toon-head',
    'toonHead': 'toon-head',
    'openpeeps': 'open-peeps',
    'open-peeps': 'open-peeps',
    'openPeeps': 'open-peeps',
  };
  const lower = key.toLowerCase();
  return map[lower] || key;
};

export const mapMembersFromApi = (items: ApiClubMember[], queue?: ApiQueueEntry[]): Member[] => {
  const queuedIds = new Set((queue || []).filter((q) => q.status === 'QUEUED').map((q) => String(q.member_id)));
  const playingIds = new Set((queue || []).filter((q) => q.status === 'PLAYING').map((q) => String(q.member_id)));

  return (items || []).map((apiMember) => {
    const role = apiMember.role.toLowerCase() as Member['role'];
    const avatarUrl = buildAvatarDataUri(apiMember.collection_name, apiMember.avatar_seed);

    return {
      id: String(apiMember.member_id),
      userId: String(apiMember.member_id),
      firstName: apiMember.first_name,
      lastName: apiMember.last_name,
      email: apiMember.email,
      nickName: apiMember.nickname || undefined,
      role,
      originalRole: apiMember.role,
      skillLevel: undefined,
      avatarUrl,
      avatarSeed: apiMember.avatar_seed || undefined,
      avatarCollection: apiMember.collection_name || undefined,
      avatarName: apiMember.avatar_name || undefined,
      hasEvaluation: role === 'coach',
      equipment: [],
      evaluations: [],
      isAvailable: queuedIds.has(String(apiMember.member_id)),
      isPlaying: playingIds.has(String(apiMember.member_id)),
      isActive: apiMember.is_active !== false,
    };
  });
};

export const mapQueueFromApi = (items: ApiQueueEntry[]) =>
  (items || []).map((entry) => ({
    userId: String(entry.member_id),
    firstName: entry.first_name,
    lastName: entry.last_name,
    position: entry.position,
    status: entry.status,
    waitStartedAt: entry.wait_started_at || null,
    elapsedSeconds: typeof entry.elapsed_seconds === 'number' ? entry.elapsed_seconds : null,
    lastQueueSeconds: typeof entry.last_queue_seconds === 'number' ? entry.last_queue_seconds : null,
    priorityCandidate: Boolean(entry.is_priority_candidate),
  }));

export const buildPlayerNameMap = (ids: Array<string | number>, memberList: Member[]) => {
  const map: Record<string, { id: string; firstName?: string; lastName?: string; role?: string; avatarUrl?: string; avatarSeed?: string; avatarCollection?: string; avatarName?: string }> = {};
  ids.forEach((pid) => {
    const key = String(pid);
    const member = memberList.find((m) => m.id === key);
    if (member) {
      map[key] = {
        id: key,
        firstName: member.firstName,
        lastName: member.lastName,
        role: member.role,
        avatarUrl: member.avatarUrl,
        avatarSeed: member.avatarSeed,
        avatarCollection: member.avatarCollection,
        avatarName: member.avatarName,
      };
    }
  });
  return map;
};

export const mapMatchHistoryFromApi = (items: any[], members: Member[]): Match[] => {
  if (!Array.isArray(items)) return [];

  return items.map((item: any) => {
    const status = String(item.status || (item.finished_at ? 'finished' : 'ongoing')).toLowerCase() as
      | 'ongoing'
      | 'finished';
    const rawPlayers = Array.isArray(item.players) ? item.players : [];
    const players = rawPlayers.map((p: any) => {
      if (p && typeof p === 'object' && 'player_id' in p) return String(p.player_id);
      return String(p);
    });
    const derivedTeam1: string[] = [];
    const derivedTeam2: string[] = [];
    rawPlayers.forEach((p: any) => {
      if (!p || typeof p !== 'object' || !('player_id' in p)) return;
      if (p.team === 1) derivedTeam1.push(String(p.player_id));
      if (p.team === 2) derivedTeam2.push(String(p.player_id));
    });
    const team1 =
      Array.isArray(item.team1) && item.team1.length
        ? item.team1.map((p: any) => String(p))
        : derivedTeam1.length
          ? derivedTeam1
          : players.slice(0, 2);
    const team2 =
      Array.isArray(item.team2) && item.team2.length
        ? item.team2.map((p: any) => String(p))
        : derivedTeam2.length
          ? derivedTeam2
          : players.slice(2, 4);
    const recordedByRaw = item.recordedBy ?? item.recorded_by;
    const recordedBy = recordedByRaw ? String(recordedByRaw) : '';
    const recordedByName = item.recordedByName || item.recorded_by_name;
    const recordedByRoleRaw = item.recordedByRole || item.recorded_by_role;
    const recordedByRole = recordedByRoleRaw ? String(recordedByRoleRaw).toLowerCase() : undefined;

    const assignedById = item.assignedById || item.assigned_by_id;
    const assignedByMemberId = item.assignedByMemberId || item.assigned_by_member_id;
    const assignedByName = item.assignedByName || item.assigned_by_name;
    const assignedByRoleRaw = item.assignedByRole || item.assigned_by_role;
    const assignedByRole = assignedByRoleRaw ? String(assignedByRoleRaw).toLowerCase() : undefined;
    const selectionMechanismRaw =
      item.selection_mechanism || item.selectionMechanism || item.selection_mode || item.selectionMode;
    const selectionMechanism = selectionMechanismRaw ? String(selectionMechanismRaw).toUpperCase() : undefined;

    const playerNamesFromApi = item.player_names || {};
    const playerNames = {
      ...Object.fromEntries(
        Object.entries(playerNamesFromApi).map(([k, v]: any) => [
          String(k),
          {
            id: String(k),
            firstName: v?.first_name,
            lastName: v?.last_name,
            role: v?.role ? String(v.role).toLowerCase() : undefined,
            avatarSeed: v?.avatar_seed,
            avatarCollection: v?.collection_name,
            avatarName: v?.avatar_name,
            avatarUrl: buildAvatarDataUri(v?.collection_name, v?.avatar_seed),
          },
        ]),
      ),
      ...buildPlayerNameMap([...new Set([...team1, ...team2, ...players])], members),
    };

    const addExtraPerson = (id?: string | number, name?: string, role?: string) => {
      const key = id ? String(id) : '';
      if (!key || playerNames[key]) return;
      const parts = (name || '').trim().split(/\s+/);
      const firstName = parts[0] || undefined;
      const lastName = parts.slice(1).join(' ') || undefined;
      playerNames[key] = {
        id: key,
        firstName,
        lastName,
        role: role ? String(role).toLowerCase() : undefined,
      };
    };

    addExtraPerson(recordedBy, recordedByName, recordedByRole);
    addExtraPerson(assignedByMemberId || assignedById, assignedByName, assignedByRole);

    return {
      id: String(item.id || item.match_id || crypto.randomUUID()),
      courtNumber: item.courtNumber || item.court_number || 0,
      courtId: item.courtId ? String(item.courtId) : item.court_id ? String(item.court_id) : undefined,
      status,
      players,
      team1,
      team2,
      score: {
        team1: Number((item.score && item.score.team1) ?? item.score_team1 ?? 0) || 0,
        team2: Number((item.score && item.score.team2) ?? item.score_team2 ?? 0) || 0,
      },
      timestamp: item.timestamp || item.finished_at || item.started_at || new Date().toISOString(),
      startedAt: item.started_at || item.startedAt || item.timestamp,
      finishedAt: item.finished_at || item.finishedAt,
      durationSeconds: item.durationSeconds || item.duration_seconds,
      recordedBy,
      recordedByName,
      recordedByRole,
      assignedById,
      assignedByMemberId,
      assignedByName,
      assignedByRole,
      selectionMechanism,
      notes: typeof item.notes === 'string' && item.notes.trim() ? item.notes : undefined,
      equipmentUsed: Array.isArray(item.equipmentUsed || item.equipment_used)
        ? (item.equipmentUsed || item.equipment_used).map((eq: any) => ({
            memberId: String(eq.memberId || eq.member_id || ''),
            shoes: eq.shoes,
            racket: eq.racket,
          }))
        : [],
      isActive: status === 'ongoing',
      playerNames,
    };
  });
};

export const mapLeaderboardFromApi = (raw: any): LeaderboardEntry[] => {
  const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
  if (!Array.isArray(items)) return [];
  return items
    .map((item: any, idx: number) => {
      const id = String(item.id ?? item.member_id ?? '');
      if (!id) return null;
      const avatarUrl = buildAvatarDataUri(item.collection_name, item.avatar_seed);
      return {
        id,
        firstName: item.first_name ?? item.firstName ?? '',
        lastName: item.last_name ?? item.lastName ?? '',
        role: item.role ? String(item.role).toLowerCase() : undefined,
        avatarUrl,
        avatarSeed: item.avatar_seed || undefined,
        avatarCollection: item.collection_name || undefined,
        avatarName: item.avatar_name || undefined,
        played: Number(item.played ?? 0),
        won: Number(item.won ?? 0),
        lost: Number(item.lost ?? 0),
        scored: Number(item.scored ?? 0),
        against: Number(item.against ?? 0),
        pd: Number(item.pd ?? 0),
        pes: Number(item.pes ?? 0),
        mps: Number(item.mps ?? 0),
        confidence: Number(item.confidence ?? 0),
        rating: Number(item.rating ?? 0),
        hitCap: Boolean(item.hit_cap ?? item.hitCap),
        finalRank: Number(item.final_rank ?? item.finalRank ?? idx + 1),
      } as LeaderboardEntry;
    })
    .filter(Boolean) as LeaderboardEntry[];
};

export const mapEvaluationsFromApi = (items: any[]): Evaluation[] => {
  const toNumber = (value: unknown) => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  const round1 = (value: number) => Math.round(value * 10) / 10;
  return (items || [])
    .map((f) => ({
      id: String(f.id),
      coachId: f.coach_id ? String(f.coach_id) : '',
      coachName: f.coach_name,
      rating: (() => {
        if (f.average_rating !== undefined && f.average_rating !== null) {
          return round1(toNumber(f.average_rating));
        }
        const vals = [
          f.technical_skills,
          f.movement,
          f.tactical_ability,
          f.physical_attributes,
          f.psychological_factors,
        ].filter((v: any) => v !== null && v !== undefined);
        if (!vals.length) return 0;
        const avg = vals.reduce((sum: number, v: any) => sum + Number(v), 0) / vals.length;
        return round1(toNumber(avg));
      })(),
      feedback: typeof f.comments === 'string' ? f.comments : '',
      quarterPlan: f.quarter_plan || f.comments || '',
      categories: {
        technical: f.technical_skills ?? undefined,
        movement: f.movement ?? undefined,
        tactical: f.tactical_ability ?? undefined,
        physical: f.physical_attributes ?? undefined,
        psychological: f.psychological_factors ?? undefined,
      },
      date: f.created_at,
      strengths: '',
      areasToImprove: '',
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const getLatestEvaluation = (evaluations?: Evaluation[]) => {
  const list = evaluations || [];
  return list.reduce<Evaluation | undefined>((latest, ev) => {
    if (!latest) return ev;
    const a = Date.parse(ev.date || '');
    const b = Date.parse(latest.date || '');
    const aVal = Number.isNaN(a) ? -Infinity : a;
    const bVal = Number.isNaN(b) ? -Infinity : b;
    return aVal > bVal ? ev : latest;
  }, undefined);
};

export const mapShoesToEquipment = (items: any[]): Equipment[] =>
  (items || []).map((s) => ({
    id: String(s.id),
    type: 'shoes',
    name: s.make,
    make: s.make,
    manufacturer: s.manufacturer || '',
    addedAt: s.created_at || new Date().toISOString(),
  }));

export const mapRacketsToEquipment = (items: any[]): Equipment[] =>
  (items || []).map((r) => ({
    id: String(r.id),
    type: 'racket',
    name: r.make,
    make: r.make,
    manufacturer: r.manufacturer || '',
    tension: r.tension ? parseInt(r.tension, 10) || undefined : undefined,
    stringType: r.string_type || undefined,
    addedAt: r.created_at || new Date().toISOString(),
  }));
